import React, { useRef, useState, useEffect } from 'react';
import { useWatch, type Control, type UseFormRegister, type FieldErrors, type UseFormSetValue } from 'react-hook-form';
import { Paperclip, X, Upload, Download } from 'lucide-react';
import type { PurchaseOrderFormData } from '../../types/purchaseOrder';
import api from '../../services/api';
import { useLanguage } from '../../features/language/useLanguage';
import { Modal } from '../common/Modal';

interface Props {
  control: Control<PurchaseOrderFormData>;
  register: UseFormRegister<PurchaseOrderFormData>;
  setValue: UseFormSetValue<PurchaseOrderFormData>;
  errors?: FieldErrors<PurchaseOrderFormData>;
  readOnly?: boolean;
}

export const POPaymentDetails: React.FC<Props> = ({ control, register, setValue, errors, readOnly = false }) => {
  const { t } = useLanguage();
  const paymentType = useWatch({ control, name: 'paymentType' });
  const paymentProofUrl = useWatch({ control, name: 'paymentProofUrl' });
  const predefinedTypes = [t('purchaseOrder.filters.options.bankTransfer'), t('purchaseOrder.filters.options.cod')];
  const paymentTypeOption = paymentType && predefinedTypes.includes(paymentType) ? paymentType : t('purchaseOrder.paymentDetails.other');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];
  const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
  const MAX_FILE_SIZE_BYTES = 1 * 1024 * 1024;

  const isImage = (path: string | null) => {
    if (!path) return false;
    const ext = path.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext || '');
  };

  const getExtension = (name: string) => name.split('.').pop()?.toLowerCase() ?? '';

  useEffect(() => {
    let objectUrl: string | null = null;
    if (paymentProofUrl && isImage(paymentProofUrl)) {
      const fetchPreview = async () => {
        try {
          const filename = getFilename(paymentProofUrl);
          const response = await api.get(`/uploads/documents/${filename}`, { responseType: 'blob' });
          objectUrl = URL.createObjectURL(new Blob([response.data]));
          setPreviewUrl(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return objectUrl;
          });
        } catch (err) {
          console.error("Failed to fetch preview", err);
        }
      };
      fetchPreview();
    } else {
      setPreviewUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [paymentProofUrl]);


  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = getExtension(file.name);
    const mimeAllowed = file.type ? ALLOWED_MIME_TYPES.includes(file.type.toLowerCase()) : true;
    if (!ALLOWED_EXTENSIONS.includes(ext) || !mimeAllowed) {
      setUploadError(t('purchaseOrder.paymentDetails.invalidFormat'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setUploadError(t('purchaseOrder.paymentDetails.fileTooLarge'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/uploads/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setValue('paymentProofUrl', response.data.path, { shouldValidate: true });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('purchaseOrder.paymentDetails.uploadFailed');
      setUploadError(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const getFilename = (path: string) => path.split('/').pop() || path;

  const handleDownload = async (path: string) => {
    try {
      const filename = getFilename(path);
      const response = await api.get(`/uploads/documents/${filename}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert(t('purchaseOrder.paymentDetails.downloadFailed'));
    }
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 h-full">
      <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">{t('purchaseOrder.paymentDetails.title')}</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">{t('purchaseOrder.paymentDetails.paymentStatus')}</label>
          <select
            data-test-id="po-create-payment-status"
            {...register('paymentStatus')}
            disabled={readOnly}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
          >
            <option value="unpaid">{t('purchaseOrder.filters.options.unpaid')}</option>
            <option value="paid">{t('purchaseOrder.filters.options.paid')}</option>
            <option value="refunded">{t('purchaseOrder.filters.options.refunded')}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('purchaseOrder.paymentDetails.paymentType')}</label>
          <div className={`mt-1 ${paymentTypeOption === t('purchaseOrder.paymentDetails.other') ? 'flex gap-2' : ''}`}>
            <select
              data-test-id="po-create-payment-type"
              value={paymentTypeOption}
              onChange={(event) => {
                const selected = event.target.value;
                if (selected !== t('purchaseOrder.paymentDetails.other')) {
                  setValue('paymentType', selected, { shouldValidate: true });
                } else if (paymentType && predefinedTypes.includes(paymentType)) {
                  setValue('paymentType', '', { shouldValidate: true });
                }
              }}
              disabled={readOnly}
              className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${paymentTypeOption === 'Other' ? 'max-w-[200px]' : ''}`}
            >
              <option value="Bank Transfer">{t('purchaseOrder.filters.options.bankTransfer')}</option>
              <option value="Cash On Delivery">{t('purchaseOrder.filters.options.cod')}</option>
              <option value={t('purchaseOrder.paymentDetails.other')}>{t('purchaseOrder.paymentDetails.other')}</option>
            </select>

            {paymentTypeOption === t('purchaseOrder.paymentDetails.other') && (
              <input
                type="text"
                {...register('paymentType')}
                disabled={readOnly}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                placeholder={t('purchaseOrder.paymentDetails.typePaymentMethod')}
              />
            )}
          </div>
          {errors?.paymentType && (
            <p className="mt-1 text-sm text-red-600 font-medium">{errors.paymentType.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('purchaseOrder.paymentDetails.proofOfPayment')}</label>
          {paymentProofUrl ? (
            <div className="mt-1 space-y-2">
              <div className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded-md">
                <Paperclip className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <button
                  type="button"
                  onClick={() => handleDownload(paymentProofUrl)}
                  className="text-sm text-primary-600 hover:underline truncate flex-1 text-left"
                  title={getFilename(paymentProofUrl)}
                >
                  {getFilename(paymentProofUrl)}
                </button>
                <button
                  type="button"
                  onClick={() => setValue('paymentProofUrl', null, { shouldValidate: true })}
                  className={`text-gray-400 ${readOnly ? 'hidden' : 'hover:text-red-500'} flex-shrink-0`}
                  title={t('purchaseOrder.paymentDetails.removeAttachment')}
                >
                  <X className="h-4 w-4" />
                </button>


              </div>
              
              {isImage(paymentProofUrl) && (
                <div 
                  className="relative group cursor-pointer border border-gray-200 rounded-md overflow-hidden aspect-video bg-white flex items-center justify-center hover:opacity-90 transition-opacity"
                  onClick={() => setIsModalOpen(true)}
                >
                  {previewUrl ? (
                    <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" />
                  ) : (
                    <div className="flex items-center gap-2 text-gray-400 text-sm italic">
                       <span className="animate-pulse">{t('inventory.lotDetail.loadingShort')}</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/5 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <span className="bg-white/90 px-3 py-1 rounded-full text-xs font-medium text-gray-700 shadow-sm">{t('purchaseOrder.actions.viewProof')}</span>
                  </div>
                </div>
              )}
            </div>
          ) : (

            <div className="mt-1">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={handleFileChange}
                className="hidden"
                data-test-id="po-payment-proof-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || readOnly}
                className="group inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-md shadow-sm transition-all duration-200 ease-out hover:bg-primary-50 hover:text-primary-700 hover:border-primary-300 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.96] active:translate-y-0 active:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-600 disabled:hover:border-gray-300 disabled:hover:translate-y-0 disabled:hover:shadow-sm motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <Upload className="h-4 w-4 transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-active:translate-y-0 motion-reduce:group-hover:translate-y-0" />
                {uploading ? t('inventory.lotDetail.uploading') : t('purchaseOrder.paymentDetails.attachFile')}
              </button>
              <p className="mt-1 text-xs text-gray-500">{t('purchaseOrder.paymentDetails.acceptedFormats')}</p>
              {uploadError && (
                <p className="mt-1 text-sm text-red-600" data-test-id="po-payment-proof-error">{uploadError}</p>
              )}
            </div>
          )}
        </div>
      </div>
      
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t('purchaseOrder.paymentDetails.proofOfPayment')}
        maxWidth="4xl"
      >
        <div className="flex items-center justify-center p-2 bg-gray-50 rounded-lg">
          {previewUrl && (
            <img 
              src={previewUrl} 
              alt="Payment Proof Full" 
              className="max-w-full max-h-[75vh] object-contain rounded-md shadow-lg" 
            />
          )}
        </div>
        <div className="mt-4 flex justify-end">
           <button 
             onClick={() => handleDownload(paymentProofUrl!)}
             className="text-sm text-primary-600 font-medium hover:underline flex items-center gap-1"
           >
             <Download className="h-4 w-4" /> {t('purchaseOrder.actions.downloadProof')}
           </button>
        </div>
      </Modal>
    </div>

  );
};
